import type { User } from 'firebase/auth';

import { APIIntegrationScope, type APIScope, IntegrationScope } from '@gitbook/api-client';

/**
 * We need to export the merged scopes as an object from
 * the api-client so it can be used in the codebase
 */
const apiScopes = { ...IntegrationScope, ...APIIntegrationScope } as const;
export { type APIScope, apiScopes };

/**
 * ID of the fake user created when authenticating session with claims.
 */
export const TOKEN_VISITOR_UID = 'visitor';

/**
 * IDs for the token used for backend operations.
 * Those IDs should be ignored for normal user operations.
 *
 * Cloud Functions that are triggered by user actions should ignore those
 * (for example creating a user profile should be only be done for a real user, not for admin-backend)
 */
export const TOKEN_BACKEND_IDS = [TOKEN_VISITOR_UID, 'admin-backend'];

/**
 * Custom claims used for authentication of a session.
 */
export interface TokenCustomClaims extends IntegrationsClaims {
    /** Grant access to a specific content (used by share links and visitor auth) */
    space?: string;
    collection?: string;
    spaces?: string[];
}

/**
 * Custom claims to identify integrations and their installations.
 */
export interface IntegrationsClaims {
    /** The ID of the integration itself */
    integration?: string;
    /** The ID of one of the integration's installations */
    installation?: string;
}

/**
 * Custom claims for an user account.
 */
export interface UserCustomClaims {
    /**
     * ID of the organization, for account created using SSO
     */
    sso?: string;

    /**
     * ID of the GitBook Staff user impersonating a GitBook user.
     */
    impersonator?: string;

    /**
     * Castle secure mode key
     */
    castleSignature?: string;

    /**
     * Intercom Messenger user hash
     */
    intercomUserHash?: string;
}

export type FirebaseUserEmailInfo = Pick<User, 'email' | 'emailVerified'>;

/**
 * Get a user's display name. Falls back to the email if no display name is present.
 */
export function getUserDisplayName(
    user: { displayName?: string | null; email?: string | null } | null | undefined,
    fallback = 'Anonymous'
): string {
    if (!user) {
        return fallback;
    }

    if (user.displayName) {
        return user.displayName;
    }

    if (user.email) {
        const nameMatch = user.email.match(/^([^@]*)@/);
        return nameMatch ? nameMatch[1] : user.email;
    }

    return fallback;
}

/**
 * Returns true if the firebase user is a siteadmin.
 */
export function isGitBookStaff(
    user: Partial<User> | Partial<FirebaseUserEmailInfo> | null
): boolean {
    return (
        !!user &&
        !!user.emailVerified &&
        !!user.email &&
        (user.email.endsWith('@gitbook.com') || user.email.endsWith('@gitbook.io'))
    );
}
